import React, { useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import AxiosUtil from '../../Utils/AxiosUtil';
import useAuth from '../../Hooks/useAuth';
import { ManagedUser, ManagedUserAccess } from '../Shared/ManagedUser';
import {
    ManagedFormRule,
    ManagedFormErrorInfo,
    ManagedApiError,
    ManagedAdminOperation,
    ManagedAdminEditorMode,
    ManagedActionModalMode,
    ManagedAdminActionMode,
    ManagedActionModal,
    ManagedActionModalState,
    buildManagedActionModalState,
    mapUserAdminModel,
} from '../Shared/ManagedHelpers';
import Administration from '../../Constants/Endpoints/Administration';
import GetUserAdminModelsRequest from '../../Interfaces/Requests/Administration/GetUserAdminModelsRequest';
import GetUserAdminModelsResponse from '../../Interfaces/Responses/Administration/GetUserAdminModelResponse';
import AUDUserRequest from '../../Interfaces/Requests/Administration/AUDUserRequest';
import {
    AddIcon,
    Button,
    Card,
    Checkbox,
    Col,
    DeleteIcon,
    Divider,
    EditIcon,
    Form,
    Input,
    Modal as ServaModal,
    Option,
    Row,
    Select,
    Space,
    Spin,
    Text,
    Title,
} from '@servahealth/serva-ui-kit';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import AUDUserAccess, { ManagedUserAccessEditResult } from './AUDUserAccess';
import GetAddUserPrecheckAdminModelRequest from '../../Interfaces/Requests/Administration/GetAddUserPrecheckAdminModelRequest';
import GetAddUserPrecheckAdminModelResponse from '../../Interfaces/Responses/Administration/GetAddUserPrecheckAdminModelResponse';
import AIContextAssistModal from '../Shared/AIContextAssistModal';
import BulbOutlinedIlluminated from '../Shared/BulbOutlinedIlluminated';
import GetAIProviderListModelResponse from '../../Interfaces/Responses/AI/GetAIProviderListModelResponse';
import GetAIExplainModelResponse from '../../Interfaces/Responses/AI/GetAIExplainModelResponse';
import GetAIUserExplainModelRequest from '../../Interfaces/Requests/AI/GetAIUserExplainModelRequest';
import GetAIProviderListModelRequest from '../../Interfaces/Requests/AI/GetAIProviderListModelRequest';

type AUDUserFormValues = {
    uDetailsFirstName: string;
    uDetailsLastName: string;
    uDetailsEmailAddress: string;
    uDetailsPassword: string;
    uDetailsUserName: string;
    uDetailsPhone: string;
    uDetailsAdminComment: string;
    uDetailsDefaultReportId: number;
    uDetailsChangePasswordRequired: boolean;
    uDetailsReportsOnly: boolean;
    uDetailsIsSystemAccount: boolean;
    uDetailsIsDemoAccount: boolean;
    uDetailsIsSecurityAdmin: boolean;
    uDetailsIsTechTeam: boolean;
};

type AUDUserProps = {};

const AUDUser: React.FC<AUDUserProps> = () => {
    const { user } = useAuth();
    const [formAUDUsers] = Form.useForm();

    const [manageActionModalState, setManageActionModalState] = useState<ManagedActionModalState>({ open: false, mode: 'info', title: '', message: '', isError: false, });

    const [addButtonClicked, setAddButtonClicked] = useState(true);
    const [updButtonClicked, setUpdButtonClicked] = useState(false);
    const [delButtonClicked, setDelButtonClicked] = useState(false);

    const [uSelectedUserID, setUSelectedUserID] = useState(-1);
    const [uSelectedUserName, setUSelectedUserName] = useState('');

    const [uSelectedUserCreatedBy, setUSelectedUserCreatedBy] = useState<number | null>(null);
    const [uSelectedUserCreatedTs, setUSelectedUserCreatedTs] = useState<Date | null>(null);
    const [uSelectedUserDeletedBy, setUSelectedUserDeletedBy] = useState<number | null>(null);
    const [uSelectedUserDeletedTs, setUSelectedUserDeletedTs] = useState<Date | null>(null);

    const [uDetailsUserAccess, setUDetailsUserAccess] = useState<ManagedUserAccess[]>([]);
    const [uAddUserPrecheckEmailAddress, setUAddUserPrecheckEmailAddress] = useState('');
    const [uAddUserPrecheckCanAdd, setUAddUserPrecheckCanAdd] = useState(false);

    const [manageUserAccessModalOpen, setManageUserAccessModalOpen] = useState(false);

    const [uIncludeDisabled] = useState(false);

    const [aiContextAssistModalOpen, setAIContextAssistModalOpen] = useState(false);
    const [aiContextAssistCurrentText, setAIContextAssistCurrentText] = useState('');
    const [aiContextAssistResultText, setAIContextAssistResultText] = useState('');
    const [aiContextAssistAncillaryItems, setAIContextAssistAncillaryItems] = useState<string[]>([]);
    const [aiContextAssistLoading, setAIContextAssistLoading] = useState(false);

    const showUInfoModal = (title: string, message: React.ReactNode, isError: boolean = true) => {
        setManageActionModalState(buildManagedActionModalState('info', title, message, isError));
    };

    const buildUErrorMessage = (prefix: string, err: unknown): string => {
        const apiError = err as ManagedApiError;

        return apiError?.response?.data?.message
            ? `${prefix}: ${apiError.response.data.message}`
            : `${prefix}: ${String(err)}`;
    };

    const buildUAddUserPrecheckSuccessMessage = (response: GetAddUserPrecheckAdminModelResponse): React.ReactNode => {
        if (response.retainBIAccountExists) {
            return (
                <>
                    A RetainBI Account already exists for this Email Address.<br />
                    {response.ssoAccountExists ? 'An SSO Account was also found.' : 'No SSO Account was found.'}<br />
                    This Email Address cannot be used to add a new User To ReatinBI.
                </>
            );
        }

        if (response.ssoAccountExists) {
            return (
                <>
                    No RetainBI Account exists for this Email Address.<br />
                    An SSO Account was found.<br />
                    This Email Address can be used to add a new User to RetainBI.
                </>
            );
        }

        return (
            <>
                No RetainBI Account exists for this Email Address.<br />
                No SSO Account was found.<br />
                This Email Address can be used to add a new User to RetainBI.
            </>
        );
    };

    const buildUAddUserPrecheckErrorMessage = (err: unknown): string => {
        return buildUErrorMessage('Unable to verify whether this User can be added.', err);
    };

    const isBlank = (value: unknown): boolean => {
        return value === undefined || value === null || String(value).trim().length === 0;
    };

    const validateRequiredUField = (fieldName: string): Promise<void> => {
        const value = formAUDUsers.getFieldValue(fieldName);
        return isBlank(value) ? Promise.reject() : Promise.resolve();
    };

    const validateUDetailsData = (rule: ManagedFormRule, value: string) => {
        switch (rule.field) {
            case 'uDetailsFirstName':
            case 'uDetailsLastName':
            case 'uDetailsEmailAddress':
            case 'uDetailsUserName':
                return validateRequiredUField(rule.field);

            case 'uDetailsPassword':
                if (addButtonClicked) {
                    return validateRequiredUField(rule.field);
                }
                return Promise.resolve();

            case 'uDetailsPhone':
            case 'uDetailsAdminComment':
            case 'uDetailsChangePasswordRequired':
            case 'uDetailsReportsOnly':
            case 'uDetailsIsSystemAccount':
            case 'uDetailsIsDemoAccount':
            case 'uDetailsIsSecurityAdmin':
            case 'uDetailsIsTechTeam':
            case 'uDetailsDefaultReportId':
                return Promise.resolve();

            default:
                return Promise.reject();
        }
    };

    const setUMode = (mode: ManagedAdminActionMode) => {
        setAddButtonClicked(mode === 'add');
        setUpdButtonClicked(mode === 'update');
        setDelButtonClicked(mode === 'delete');
    };

    const getUOperation = (): ManagedAdminOperation => {
        if (addButtonClicked) return 'Add';
        if (updButtonClicked) return 'Upd';
        if (delButtonClicked) return 'Del';
        return '';
    };

    const getUActionModalMode = (): ManagedActionModalMode => {
        if (addButtonClicked) return 'add';
        if (updButtonClicked) return 'update';
        if (delButtonClicked) return 'delete';
        return 'info';
    };

    const getUActionLabel = (): string => {
        if (addButtonClicked) return 'Add';
        if (updButtonClicked) return 'Update';
        if (delButtonClicked) return 'Delete';
        return 'Manage';
    };

    const getUActionColor = (): string => {
        if (addButtonClicked) return 'cornflowerblue';
        if (updButtonClicked) return 'darkgray';
        if (delButtonClicked) return 'red';
        return 'black';
    };

    const getUActionTargetName = (): string => {
        return uSelectedUserName || formAUDUsers.getFieldValue('uDetailsEmailAddress') || formAUDUsers.getFieldValue('uDetailsUserName') || '';
    };

    const showUResultModal = (isError: boolean, message: string) => {
        setManageActionModalState(buildManagedActionModalState(getUActionModalMode(), `${getUActionLabel()} User`, message, isError));
    };

    const resetUAddUserPrecheck = () => {
        setUAddUserPrecheckEmailAddress('');
        setUAddUserPrecheckCanAdd(false);
    };

    const clearUSelection = () => {
        setUSelectedUserID(-1);
        setUSelectedUserName('');
        setUSelectedUserCreatedBy(null);
        setUSelectedUserCreatedTs(null);
        setUSelectedUserDeletedBy(null);
        setUSelectedUserDeletedTs(null);
        setUMode('add');
        setManageUserAccessModalOpen(false);
        resetUAddUserPrecheck();
        setUBlankForm();
    };

    const buildAUDUserRequest = (): AUDUserRequest => {
        const uDetailsUserNameL = formAUDUsers.getFieldValue('uDetailsUserName');
        const uDetailsEmailAddressL = formAUDUsers.getFieldValue('uDetailsEmailAddress');
        const uDetailsPasswordL = formAUDUsers.getFieldValue('uDetailsPassword');
        const uDetailsAdminCommentL = formAUDUsers.getFieldValue('uDetailsAdminComment');
        const uDetailsIsSystemAccountL = formAUDUsers.getFieldValue('uDetailsIsSystemAccount');
        const uDetailsFirstNameL = formAUDUsers.getFieldValue('uDetailsFirstName');
        const uDetailsLastNameL = formAUDUsers.getFieldValue('uDetailsLastName');
        const uDetailsPhoneL = formAUDUsers.getFieldValue('uDetailsPhone');
        const uDetailsIsDemoAccountL = formAUDUsers.getFieldValue('uDetailsIsDemoAccount');
        const uDetailsDefaultReportId = formAUDUsers.getFieldValue('uDetailsDefaultReportId');
        const uDetailsChangePasswordRequiredL = formAUDUsers.getFieldValue('uDetailsChangePasswordRequired');
        const uDetailsReportsOnlyL = formAUDUsers.getFieldValue('uDetailsReportsOnly');
        const uDetailsIsSecurityAdminL = formAUDUsers.getFieldValue('uDetailsIsSecurityAdmin');
        const uDetailsIsTechTeamL = formAUDUsers.getFieldValue('uDetailsIsTechTeam');
        
        const selectedUser = getSelectedUser();
        const effectivePassword = addButtonClicked
            ? uDetailsPasswordL ?? ''
            : (uDetailsPasswordL ? uDetailsPasswordL : selectedUser?.password ?? '');

        return {
            id: uSelectedUserID,
            userName: uDetailsUserNameL ?? '',
            email: uDetailsEmailAddressL ?? '',
            password: effectivePassword,
            adminComment: uDetailsAdminCommentL ?? '',
            isSystemAccount: uDetailsIsSystemAccountL ?? false,
            firstName: uDetailsFirstNameL ?? '',
            lastName: uDetailsLastNameL ?? '',
            phone: uDetailsPhoneL ?? '',
            created_By: addButtonClicked ? user?.id ?? 0 : uSelectedUserCreatedBy,
            modified_By: user?.id ?? 0,
            deleted_By: delButtonClicked ? user?.id ?? 0 : uSelectedUserDeletedBy,
            created_Ts: addButtonClicked ? new Date() : uSelectedUserCreatedTs,
            modified_Ts: new Date(),
            deleted_Ts: delButtonClicked ? new Date() : uSelectedUserDeletedTs,
            isDemo: uDetailsIsDemoAccountL ?? false,
            defaultReportId: uDetailsDefaultReportId ?? 0,
            changePasswordRequired: uDetailsChangePasswordRequiredL ?? true,
            reportsOnly: uDetailsReportsOnlyL ?? true,
            securityAdmin: uDetailsIsSecurityAdminL ?? false,
            techTeam: uDetailsIsTechTeamL ?? false,
            operation: getUOperation(),
        };
    };

    const { data: aiProviders = [], isLoading: isGetAIProvidersLoading } = useQuery<GetAIProviderListModelResponse[], unknown>(
        ['aud-user-ai-providers'],
        async () => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}/api/AI/httpGet_GetAIProviders`;
                const requestData: GetAIProviderListModelRequest = {};

                return await AxiosUtil.get<GetAIProviderListModelResponse[], GetAIProviderListModelRequest>(url, requestData);
            } catch (err) {
                console.error('AUD_USER_AI_PROVIDERS: [Get AI Providers] Internal Error: ', err);
                return Promise.reject(err);
            }
        },
        {
            onError: (err: unknown) => {
                console.error('AUD_USER_AI_PROVIDERS: [Get AI Providers] Internal Error: ', err);
                setManageActionModalState(
                    buildManagedActionModalState(
                        'info',
                        'AI Provider Load Error',
                        'Unable to load AI Providers for User Explain.',
                        true
                    )
                );
            },
        }
    );

    const getAIContextAssistProvider = (): GetAIProviderListModelResponse | undefined => {
        const providerNamePhrase = 'User Explain';

        return aiProviders.find((provider) =>
            provider.name.toLowerCase().includes(providerNamePhrase.toLowerCase()) ||
            provider.displayName.toLowerCase().includes(providerNamePhrase.toLowerCase())
        );
    };

    const { mutate: getAIContextAssistPrompt } = useMutation(
        async ({ externalVendorID, tenantID, userID, prompt }: GetAIUserExplainModelRequest) => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}/api/AI/httpPost_AIUserExplain`;
                const requestData: GetAIUserExplainModelRequest = {
                    externalVendorID: externalVendorID,
                    tenantID: tenantID,
                    userID: userID,
                    prompt: prompt,
                };

                return await AxiosUtil.post<GetAIExplainModelResponse, GetAIUserExplainModelRequest>(url, requestData);
            } catch (err) {
                console.error('AUD_USER_AI_CONTEXT_ASSIST: [AI User Explain] Internal Error: ', err);
                return Promise.reject(err);
            }
        },
        {
            onSuccess: (response: GetAIExplainModelResponse) => {
                setAIContextAssistResultText((response.text ?? '').trim());
                setAIContextAssistAncillaryItems([
                    response.success ? 'AI User Explain returned successfully.' : 'AI User Explain request did not succeed.',
                    response.message ?? '',
                ].filter((item) => item.trim().length > 0));
                setAIContextAssistLoading(false);
            },
            onError: (err: unknown) => {
                console.error('AUD_USER_AI_CONTEXT_ASSIST: [AI User Explain] Internal Error: ', err);
                setAIContextAssistAncillaryItems([
                    'Unable to complete the AI User Explain request.',
                ]);
                setAIContextAssistLoading(false);
            },
        }
    );

    const { data: users = [], isLoading: isGetUsersLoading, refetch: refetchUsers } = useQuery<GetUserAdminModelsResponse[], Error, ManagedUser[]>(
        ['getUserAdminModels', uIncludeDisabled],
        async (): Promise<GetUserAdminModelsResponse[]> => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.GET_USER_ADMIN_MODELS}`;
                const requestData: GetUserAdminModelsRequest = { gua_includeDisabled: uIncludeDisabled };
                return await AxiosUtil.get<GetUserAdminModelsResponse[], GetUserAdminModelsRequest>(url, requestData);
            } catch (err) {
                console.error('GET_USER_ADMIN_MODELS: [Get User Admin Models AUD] Internal Error: ', err);
                throw err;
            }
        },
        {
            onError: (err: Error) => {
                console.error('GET_USER_ADMIN_MODELS: [Get User Admin Models AUD] Internal Error: ', err);
                showUInfoModal('User Load Error', buildUErrorMessage('Unable to load Users for Manage Users.', err));
            },
            select: (response: GetUserAdminModelsResponse[]): ManagedUser[] =>
                response
                    .map(mapUserAdminModel)
                    .sort((a, b) => (a.firstName + ' ' + a.lastName).localeCompare(b.firstName + ' ' + b.lastName)),
        }
    );

    const { mutate: audUser, isLoading: isAUDUserLoading, reset: resetAUDUser } = useMutation<boolean, Error, void>(
        async (): Promise<boolean> => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.AUD_USER_DETAILS_BY_ID}`;
                const requestData: AUDUserRequest = buildAUDUserRequest();
                const responseData = await AxiosUtil.post<boolean, AUDUserRequest>(url, requestData);
                return responseData;
            } catch (err) {
                console.error('AUD_USER_DETAILS_BY_ID: [AUD User t/c] Internal Error: ', err);
                throw err;
            }
        },
        {
            onError: (err: Error) => {
                const userName = getUActionTargetName();
                console.error('AUD_USER_DETAILS_BY_ID: [AUD User o/e] Internal Error: ', err);
                showUResultModal(true, `${getUActionLabel()} User: ${userName} Failed. Please Contact Application Support`);
            },
            onSuccess: async (response: boolean) => {
                const userName = getUActionTargetName();
                const actionLabel = getUActionLabel();

                if (delButtonClicked) {
                    clearUSelection();
                    await refetchUsers();
                    showUResultModal(false, `${actionLabel} User: ${userName} Succeeded.`);
                    return;
                }

                const refetchResult = await refetchUsers();
                const refreshedUser = refetchResult.data?.find((eachUser) => eachUser.id === uSelectedUserID);

                if (refreshedUser) {
                    setUFormFromSelectedUser(refreshedUser);
                }

                showUResultModal(false, `${actionLabel} User: ${userName} Succeeded.`);
            },
        }
    );

    const { mutate: getAddUserPrecheck, isLoading: isGetAddUserPrecheckLoading } = useMutation<GetAddUserPrecheckAdminModelResponse, Error, string>(
        async (emailAddress: string): Promise<GetAddUserPrecheckAdminModelResponse> => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.GET_ADD_USER_PRECHECK_ADMIN_MODEL}`;
                const requestData: GetAddUserPrecheckAdminModelRequest = {
                    Email: emailAddress,
                };

                const responseData = await AxiosUtil.post<GetAddUserPrecheckAdminModelResponse, GetAddUserPrecheckAdminModelRequest>(url, requestData);
                return responseData;
            } catch (err) {
                console.error('GET_ADD_USER_PRECHECK_ADMIN_MODEL: [Get Add User Precheck AUD] Internal Error: ', err);
                throw err;
            }
        },
        {
            onSuccess: (response: GetAddUserPrecheckAdminModelResponse, emailAddress: string) => {
                const canAddUser = !response.retainBIAccountExists;

                setUAddUserPrecheckEmailAddress(emailAddress);
                setUAddUserPrecheckCanAdd(canAddUser);

                showUInfoModal(
                    canAddUser ? 'User Can Be Added' : 'User Already Exists',
                    buildUAddUserPrecheckSuccessMessage(response),
                    !canAddUser
                );
            },
            onError: (err: Error) => {
                showUInfoModal('User Precheck Error', buildUAddUserPrecheckErrorMessage(err));
            },
        }
    );

    const handleUAddUserPrecheck = () => {
        const emailAddress = formAUDUsers.getFieldValue('uDetailsEmailAddress');

        if (!addButtonClicked) {
            showUInfoModal('User Precheck Not Available', 'User precheck is only available while adding a User.');
            return;
        }

        if (isBlank(emailAddress)) {
            showUInfoModal('Email Address Required', 'Enter an Email Address before verifying whether this User can be added.');
            return;
        }

        const trimmedEmailAddress = String(emailAddress).trim();

        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmailAddress)) {
            showUInfoModal('Email Address Invalid', 'Enter a valid Email Address before verifying whether this User can be added.');
            return;
        }

        resetUAddUserPrecheck();

        getAddUserPrecheck(trimmedEmailAddress);
    };

    const buildUserExplainSelectionSummary = (selectedUser: ManagedUser | undefined): string => {
        if (!selectedUser) {
            return '';
        }

        return [
            `User ID: ${selectedUser.id}`,
            `User Name: ${selectedUser.firstName} ${selectedUser.lastName}`.trim(),
            '',
            'The detailed User Explain context is built by the backend.',
        ].join('\n');
    };

    const handleOpenAIUserExplainModal = () => {
        const selectedUser = getSelectedUser();

        setAIContextAssistCurrentText(buildUserExplainSelectionSummary(selectedUser));
        setAIContextAssistResultText('');
        setAIContextAssistAncillaryItems([]);
        setAIContextAssistModalOpen(true);
    };

    const handleAskAIContextAssist = (requestText: string) => {
        const aiContextAssistProvider = getAIContextAssistProvider();

        if (!aiContextAssistProvider) {
            setAIContextAssistAncillaryItems([
                'Unable to find an AI Provider matching User Explain.',
            ]);
            return;
        }

        if (uSelectedUserID <= 0) {
            setAIContextAssistAncillaryItems([
                'Select a User before asking AI to explain it.',
            ]);
            return;
        }

        setAIContextAssistLoading(true);
        setAIContextAssistResultText('');
        setAIContextAssistAncillaryItems([]);

        getAIContextAssistPrompt({
            externalVendorID: aiContextAssistProvider.externalVendorID,
            tenantID: null,
            userID: uSelectedUserID,
            prompt: requestText,
        });
    };

    const handleCopyAIContextAssistResult = (resultText: string) => {
        navigator.clipboard.writeText(resultText);
    };

    const getUserAccessEditorContext = () => {
        const selectedUser = getSelectedUser();
        const formFirstName = formAUDUsers.getFieldValue('uDetailsFirstName') ?? '';
        const formLastName = formAUDUsers.getFieldValue('uDetailsLastName') ?? '';
        const formUserName = `${formFirstName} ${formLastName}`.trim();

        return {
            userID: selectedUser?.id ?? uSelectedUserID,
            userName: formUserName || uSelectedUserName || `${selectedUser?.firstName ?? ''} ${selectedUser?.lastName ?? ''}`.trim(),
            userAccesses: uDetailsUserAccess,
        };
    };

    const openUUserAccessEditor = () => {
        if (uSelectedUserID <= 0) {
            showUInfoModal(
                'User Required',
                'Please select a User before managing linked Reports.'
            );

            return;
        }

        setManageUserAccessModalOpen(true);
    };

    const closeUUserAccessEditor = () => {
        setManageUserAccessModalOpen(false);
    };

    const onUUserAccess = () => {
        openUUserAccessEditor();
    };

    const applyUserAccessEditResult = (
        existingUserAccess: ManagedUserAccess[],
        result: ManagedUserAccessEditResult
    ): ManagedUserAccess[] => {
        switch (result.operation) {
            case 'add':
            case 'update':
            case 'delete':
                return result.userAccesses;

            default:
                return existingUserAccess;
        }
    };

    const handleSetUserAccess = (result: ManagedUserAccessEditResult) => {
        setUDetailsUserAccess((currentUserAccess) =>
            applyUserAccessEditResult(currentUserAccess, result)
        );
    };
    
    const onFinishFailed = (_errorInfo: ManagedFormErrorInfo<AUDUserFormValues>) => {
        showUInfoModal('User Validation Error', 'Please correct the highlighted User fields and try again.');
    };

    const onFinish = () => {
        if (addButtonClicked) {
            const emailAddress = String(formAUDUsers.getFieldValue('uDetailsEmailAddress') ?? '').trim();

            if (!uAddUserPrecheckCanAdd || uAddUserPrecheckEmailAddress !== emailAddress) {
                showUInfoModal(
                    'User Precheck Required',
                    'Verify that this Email Address can be added before adding the User.'
                );
                return;
            }
        }

        audUser();
    }

    const getSelectedUser = (selectedUserID: number = uSelectedUserID): ManagedUser | undefined => {
        return users.find((eachUser) => eachUser.id === selectedUserID);
    };

    const handleUSelectionChange = (selectedUserID: number) => {
        setUSelectedUserID(selectedUserID);

        const selectedUser = getSelectedUser(selectedUserID);

        setUSelectedUserName(`${selectedUser?.firstName ?? ''} ${selectedUser?.lastName ?? ''}`.trim());

        setUSelectedUserCreatedBy(selectedUser?.createdBy ?? null);
        setUSelectedUserCreatedTs(selectedUser?.createdTs ?? null);
        setUSelectedUserDeletedBy(selectedUser?.deletedBy ?? null);
        setUSelectedUserDeletedTs(selectedUser?.deletedTs ?? null);

        setUMode('add');
        resetUAddUserPrecheck();
        setUBlankForm();
    };

    const setUBlankForm = () => {
        formAUDUsers.setFieldValue('uDetailsFirstName', '');
        formAUDUsers.setFieldValue('uDetailsLastName', '');
        formAUDUsers.setFieldValue('uDetailsEmailAddress', '');
        formAUDUsers.setFieldValue('uDetailsPassword', '');
        formAUDUsers.setFieldValue('uDetailsUserName', '');
        formAUDUsers.setFieldValue('uDetailsPhone', '');
        formAUDUsers.setFieldValue('uDetailsAdminComment', '');
        formAUDUsers.setFieldValue('uDetailsDefaultReportId', 0);
        formAUDUsers.setFieldValue('uDetailsChangePasswordRequired', true);
        formAUDUsers.setFieldValue('uDetailsReportsOnly', true);
        formAUDUsers.setFieldValue('uDetailsIsSystemAccount', false);
        formAUDUsers.setFieldValue('uDetailsIsDemoAccount', false);
        formAUDUsers.setFieldValue('uDetailsIsSecurityAdmin', false);
        formAUDUsers.setFieldValue('uDetailsIsTechTeam', false);

        setUDetailsUserAccess([]);
    };

    const setUFormFromSelectedUser = (userOverride?: ManagedUser) => {
        const selectedUser = userOverride ?? getSelectedUser();

        formAUDUsers.setFieldValue('uDetailsFirstName', selectedUser?.firstName ?? '');
        formAUDUsers.setFieldValue('uDetailsLastName', selectedUser?.lastName ?? '');
        formAUDUsers.setFieldValue('uDetailsEmailAddress', selectedUser?.email ?? '');
        formAUDUsers.setFieldValue('uDetailsPassword', '');
        formAUDUsers.setFieldValue('uDetailsUserName', selectedUser?.userName ?? '');
        formAUDUsers.setFieldValue('uDetailsPhone', selectedUser?.phone ?? '');
        formAUDUsers.setFieldValue('uDetailsAdminComment', selectedUser?.adminComment ?? '');
        formAUDUsers.setFieldValue('uDetailsDefaultReportId', selectedUser?.defaultReportId ?? 0);
        formAUDUsers.setFieldValue('uDetailsChangePasswordRequired', selectedUser?.changePasswordRequired ?? true);
        formAUDUsers.setFieldValue('uDetailsReportsOnly', selectedUser?.reportsOnly ?? true);
        formAUDUsers.setFieldValue('uDetailsIsSystemAccount', selectedUser?.isSystemAccount ?? false);
        formAUDUsers.setFieldValue('uDetailsIsDemoAccount', selectedUser?.isDemo ?? false);
        formAUDUsers.setFieldValue('uDetailsIsSecurityAdmin', selectedUser?.securityAdmin ?? false);
        formAUDUsers.setFieldValue('uDetailsIsTechTeam', selectedUser?.techTeam ?? false);

        setUDetailsUserAccess(selectedUser?.userAccesses ?? []);
    };

    const handleUUserResetForm = (mode: ManagedAdminEditorMode) => {
        if (mode === 'blank') {
            setUBlankForm();
            return;
        }

        setUFormFromSelectedUser();
    };

    //=================================================================
    //=================================================================
    
    const handleUAddUser = () => {
        resetAUDUser();
        clearUSelection();
    };

    //=================================================================
    //=================================================================

    const handleUUpdUser = () => {
        resetAUDUser();
        setUMode('update');
        handleUUserResetForm('selected');
    };

    //=================================================================
    //=================================================================

    const handleUDelUser = () => {
        resetAUDUser();
        setUMode('delete');
        handleUUserResetForm('selected');
    };

    //=================================================================
    //=================================================================

    const handleManageModalOk = () => {
        setManageActionModalState((prev) => ({
            ...prev,
            open: false,
        }));
    };

    const onUCancel = () => {
        resetAUDUser();
        clearUSelection();
    };

    const contentStyle: React.CSSProperties = {
        lineHeight: '95%',
        textAlign: 'center',
        color: 'black',
        backgroundColor: 'azure',
        borderRadius: '10px',
        border: `1px solid black`,
        marginTop: '8px',
        height: '470px'
    };
    
    return (
        <Card className='shadow-card admin-full-page-card' size='small'>
            <ManagedActionModal
                modalState={manageActionModalState}
                onOk={handleManageModalOk}
            />
            <AIContextAssistModal
                open={aiContextAssistModalOpen}
                title='AI User Explain'
                currentSectionTitle='Current User Context'
                requestSectionTitle='What do you want explained?'
                resultSectionTitle='User Explanation'
                ancillarySectionTitle='Ancillary Information'
                currentText={aiContextAssistCurrentText}
                resultText={aiContextAssistResultText}
                ancillaryItems={aiContextAssistAncillaryItems}
                requestPlaceholder='Optionally describe what part of this User administration record you want explained.'
                askButtonText='Explain User'
                useButtonText='Use Suggestion'
                loading={aiContextAssistLoading || isGetAIProvidersLoading}
                allowUseSuggestion={false}
                onAskAI={handleAskAIContextAssist}
                onCopy={handleCopyAIContextAssistResult}
                onUseSuggestion={() => undefined}
                onClose={() => setAIContextAssistModalOpen(false)}
            />
            <ServaModal
                closeIcon={<CloseCircleOutlined></CloseCircleOutlined>}
                destroyOnHidden={true}
                hideButtons={true}
                width='75%'
                open={manageUserAccessModalOpen}
                onCancel={closeUUserAccessEditor}
            >
                {
                    manageUserAccessModalOpen &&
                    (
                        <AUDUserAccess
                            userID={getUserAccessEditorContext().userID}
                            userName={getUserAccessEditorContext().userName}
                            userAccesses={getUserAccessEditorContext().userAccesses}
                            handleSetUserAccess={handleSetUserAccess}
                            close={closeUUserAccessEditor}
                        />
                    )
                }
            </ServaModal>
            <Spin spinning={
                            isGetUsersLoading ||
                            isAUDUserLoading ||
                            isGetAddUserPrecheckLoading
                           }>
                <Space direction='horizontal' style={{justifyContent: 'center', padding: '4px', width: '100%'}}>
                    <Text style={{height: '100%', width: '100%'}}>
                        <Title style={{backgroundColor: 'rgb(00, 55, 90)', borderRadius: '10px', color: 'white', height: '100%', padding: '8px', width: '100%'}} level={4}>Manage Users</Title>
                    </Text>
                </Space>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={1} />
                    <Col span={22}>
                        <div>
                            <Space direction='horizontal' style={{width: '100%', justifyContent: 'left'}}>
                                <Text italic>Select an action below to proceed.</Text>
                            </Space>
                            <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                        </div>
                    </Col>
                    <Col span={1} />
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={2} />
                    <Col span={8}>
                        <Text italic style={{ float: 'right' }}>Select User to Update or Delete:</Text>
                    </Col>
                    <Col span={10}>
                        <Select
                            filterOption={(input, option) => (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())}
                            placeholder={'Select a User'}
                            showSearch
                            styles={{ popup: { root: { width: 500 } } }}
                            style={{paddingTop: '4px', width: '100%'}}
                            value={uSelectedUserID === -1 ? undefined : uSelectedUserID}
                            onChange={handleUSelectionChange}
                        >
                            {users.map(
                                (eachUser) => {
                                    return (
                                        <Option
                                            key={eachUser.id}
                                            value={eachUser.id}
                                            label={eachUser.firstName + ' ' + eachUser.lastName}
                                        >
                                            {eachUser.firstName + ' ' + eachUser.lastName}
                                        </Option>
                                    );
                                }
                            )}
                        </Select>
                    </Col>
                    <Col span={1}>
                        <Button
                            disabled={uSelectedUserID === -1 || isGetAIProvidersLoading}
                            icon={<BulbOutlinedIlluminated />}
                            style={{
                                minWidth: '32px',
                                padding: '0px',
                                marginTop: '4px',
                            }}
                            title='AI User Explain'
                            type='default'
                            onClick={handleOpenAIUserExplainModal}
                        />
                    </Col>
                    <Col span={3} />
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={2} />
                    <Col span={3}>
                        <Button
                            icon={<AddIcon />}
                            style={{width: '95%', marginBottom: '4px', float: 'left',}}
                            type='primary'
                            onClick={handleUAddUser}
                        >
                            Add User
                        </Button>
                    </Col>
                    <Col span={9} />
                    <Col span={3}>
                        <Button
                            disabled={uSelectedUserID === -1}
                            icon={<EditIcon />}
                            style={{width: '95%', backgroundColor: 'darkgray', float: 'right', marginBottom: '4px',}}
                            type='primary'
                            onClick={handleUUpdUser}
                        >
                            Update User
                        </Button>
                    </Col>
                    <Col span={3}>
                        <Button
                            disabled={uSelectedUserID === -1}
                            icon={<DeleteIcon />}
                            style={{width: '95%', backgroundColor: 'red', float: 'right', marginBottom: '4px',}}
                            type='primary'
                            onClick={handleUDelUser}
                        >
                            Delete User
                        </Button>
                    </Col>
                    <Col span={4} />
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={1} />
                    <Col span={22}>
                        <div>
                            <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                        </div>
                    </Col>
                    <Col span={1} />
                </Row>
                <Form
                    form={formAUDUsers}
                    initialValues={{
                        uDetailsDefaultReportId: 0,
                        uDetailsChangePasswordRequired: true,
                        uDetailsReportsOnly: true,
                        uDetailsIsSystemAccount: false,
                        uDetailsIsDemoAccount: false,
                        uDetailsIsSecurityAdmin: false,
                        uDetailsIsTechTeam: false,
                    }}
                    onFinish={onFinish}
                    onFinishFailed={onFinishFailed}
                >
                    <Row gutter={16} className='retainbi-compact-row'>
                        <Col span={1} />
                        <Col span={22}>
                            <div style={contentStyle}>
                                <Row gutter={16} className='retainbi-compact-row'>
                                    <Col span={22}>
                                        <Divider orientation='left' orientationMargin={50} variant='dotted' style={{ borderColor: 'black', color: getUActionColor() }}>
                                            {addButtonClicked ? 'Add User' :
                                                (updButtonClicked ? 'Update User: [' + uSelectedUserID + '] ' + uSelectedUserName :
                                                    (delButtonClicked ? 'Delete User: [' + uSelectedUserID + '] ' + uSelectedUserName : ''))}
                                        </Divider>
                                    </Col>
                                    <Col span={2} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Button
                                            style={{ color: getUActionColor(), fontStyle: 'italic' }}
                                            type='link'
                                            onClick={() => handleUUserResetForm(addButtonClicked ? 'blank' : 'selected')}
                                        >
                                            Reset Form
                                        </Button>
                                    </Col>
                                </Row>
                                <Row gutter={16} className='retainbi-compact-row'>
                                    <Col span={8}>
                                        <Form.Item
                                            key='udetailsfirstname'
                                            name='uDetailsFirstName'
                                            label='First Name'
                                            labelCol={{ span: 9 }}
                                            rules={[
                                                {
                                                    message: 'First Name required.',
                                                    required: true,
                                                    validator: validateUDetailsData,
                                                },
                                            ]}
                                            style={{marginBottom: '4px'}}
                                        >
                                            <Input
                                                disabled={delButtonClicked}
                                                placeholder='Enter First Name'
                                                style={{color: delButtonClicked ? 'gray' : 'black'}}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={8}>
                                        <Form.Item
                                            key='udetailslastname'
                                            name='uDetailsLastName'
                                            label='Last Name'
                                            labelCol={{ span: 9 }}
                                            rules={[
                                                {
                                                    message: 'Last name required.',
                                                    required: true,
                                                    validator: validateUDetailsData,
                                                },
                                            ]}
                                            style={{marginBottom: '4px'}}
                                        >
                                            <Input
                                                disabled={delButtonClicked}
                                                placeholder='Enter Last Name'
                                                style={{ color: delButtonClicked ? 'gray' : 'black' }}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={6}>
                                        <Form.Item
                                            key='udetailsphone'
                                            name='uDetailsPhone'
                                            label='Phone'
                                            labelCol={{ span: 9 }}
                                            style={{ marginBottom: '4px' }}
                                        >
                                            <Input
                                                disabled={delButtonClicked}
                                                placeholder='Enter Phone'
                                                style={{ color: delButtonClicked ? 'gray' : 'black' }}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={2} />
                                </Row>
                                <Row gutter={16} className='retainbi-compact-row'>
                                    <Col span={8}>
                                        <Form.Item
                                            key='udetailsemailaddress'
                                            name='uDetailsEmailAddress'
                                            label='Email Address'
                                            labelCol={{ span: 9 }}
                                            rules={[
                                                {
                                                    message: 'Email address required.',
                                                    required: true,
                                                    validator: validateUDetailsData,
                                                },
                                            ]}
                                            style={{ marginBottom: '4px' }}
                                        >
                                            <Space.Compact style={{ width: '100%' }}>
                                                <Input
                                                    disabled={delButtonClicked}
                                                    placeholder='Enter Email Address'
                                                    readOnly={updButtonClicked || delButtonClicked}
                                                    style={{ color: delButtonClicked ? 'gray' : 'black' }}
                                                    onChange={resetUAddUserPrecheck}
                                                />
                                                <Button
                                                    disabled={!addButtonClicked || delButtonClicked || isGetAddUserPrecheckLoading}
                                                    icon={<CheckCircleOutlined style={{ color: 'blue' }} />}
                                                    title='Verify User Can Be Added'
                                                    onClick={handleUAddUserPrecheck}
                                                />
                                            </Space.Compact>
                                        </Form.Item>
                                    </Col>
                                    <Col span={8}>
                                        <Form.Item
                                            key='udetailsusername'
                                            name='uDetailsUserName'
                                            label='User Name'
                                            labelCol={{ span: 9 }}
                                            rules={[
                                                {
                                                    message: 'User name required.',
                                                    required: true,
                                                    validator: validateUDetailsData,
                                                },
                                            ]}
                                            style={{ marginBottom: '4px' }}
                                        >
                                            <Input
                                                disabled={delButtonClicked}
                                                placeholder='Enter User Name'
                                                readOnly={updButtonClicked || delButtonClicked}
                                                style={{ color: delButtonClicked ? 'gray' : 'black' }}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={6}>
                                        <Form.Item
                                            key='udetailspassword'
                                            name='uDetailsPassword'
                                            label='Password'
                                            labelCol={{ span: 9 }}
                                            rules={[
                                                {
                                                    message: addButtonClicked
                                                        ? 'Password required.'
                                                        : 'Enter a password only if you want to change it.',
                                                    required: true,
                                                    validator: validateUDetailsData,
                                                },
                                            ]}
                                            style={{ marginBottom: '4px' }}
                                        >
                                            <Input
                                                disabled={delButtonClicked}
                                                placeholder={addButtonClicked ? 'Enter Password' : 'Leave blank to keep existing password'}
                                                style={{ color: delButtonClicked ? 'gray' : 'black' }}
                                                type='password'
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={2} />
                                </Row>
                                <Row gutter={16} className='retainbi-compact-row'>
                                    <Col span={22}>
                                        <Form.Item
                                            key='udetailsadmincomment'
                                            name='uDetailsAdminComment'
                                            label='Admin Comment'
                                            labelCol={{ span: 3 }}
                                            style={{ marginBottom: '4px' }}
                                        >
                                            <Input
                                                disabled={delButtonClicked}
                                                placeholder='Enter Admin Comment'
                                                style={{ color: delButtonClicked ? 'gray' : 'black' }}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={2} />
                                </Row>
                                <Row gutter={16} className='retainbi-compact-row'>
                                    <Col span={1} />
                                    <Col span={22}>
                                        <Divider style={{ borderTop: '1px solid black', margin: '1px' }} />
                                    </Col>
                                    <Col span={1} />
                                </Row>
                                <Row gutter={16} className='retainbi-compact-row'>
                                    <Col span={4}>
                                        <Form.Item
                                            key='udetailschangepasswordrequired'
                                            name='uDetailsChangePasswordRequired'
                                            valuePropName='checked'
                                            style={{ marginBottom: '4px' }}
                                        >
                                            <Checkbox disabled={delButtonClicked}>
                                                Change Password
                                            </Checkbox>
                                        </Form.Item>
                                    </Col>
                                    <Col span={4}>
                                        <Form.Item
                                            key='udetailsreportsonly'
                                            name='uDetailsReportsOnly'
                                            valuePropName='checked'
                                            style={{ marginBottom: '4px' }}
                                        >
                                            <Checkbox disabled={delButtonClicked}>
                                                Reports Only
                                            </Checkbox>
                                        </Form.Item>
                                    </Col>
                                    <Col span={4}>
                                        <Form.Item
                                            key='udetailsissystemaccount'
                                            name='uDetailsIsSystemAccount'
                                            valuePropName='checked'
                                            style={{ marginBottom: '4px' }}
                                        >
                                            <Checkbox disabled={delButtonClicked}>
                                                Is System Account
                                            </Checkbox>
                                        </Form.Item>
                                    </Col>
                                    <Col span={4}>
                                        <Form.Item
                                            key='udetailsisdemoaccount'
                                            name='uDetailsIsDemoAccount'
                                            valuePropName='checked'
                                            style={{ marginBottom: '4px' }}
                                        >
                                            <Checkbox disabled={delButtonClicked}>
                                                Is Demo Account
                                            </Checkbox>
                                        </Form.Item>
                                    </Col>
                                    <Col span={4}>
                                        <Form.Item
                                            key='udetailsissecurityadmin'
                                            name='uDetailsIsSecurityAdmin'
                                            valuePropName='checked'
                                            style={{ marginBottom: '4px' }}
                                        >
                                            <Checkbox disabled={delButtonClicked}>
                                                Is Security Admin
                                            </Checkbox>
                                        </Form.Item>
                                    </Col>
                                    <Col span={4}>
                                        <Form.Item
                                            key='udetailsistechteam'
                                            name='uDetailsIsTechTeam'
                                            valuePropName='checked'
                                            style={{ marginBottom: '4px' }}
                                        >
                                            <Checkbox disabled={delButtonClicked}>
                                                Is Tech Team
                                            </Checkbox>
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Row gutter={16} className='retainbi-compact-row'>
                                    <Col span={1} />
                                    <Col span={22}>
                                        <div>
                                            <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                                        </div>
                                    </Col>
                                    <Col span={1} />
                                </Row>
                                <Row gutter={16} className='retainbi-compact-row' style={{ paddingTop: '4px' }}>
                                    <Col span={6}>
                                        <Form.Item
                                            key='uadetailsaccesses'
                                            name='uaDetailsAccesses'
                                            style={{marginBottom: '4px'}}
                                        >
                                            <Button
                                                disabled={addButtonClicked || delButtonClicked || uSelectedUserID === -1}
                                                style={{ width: '90%', backgroundColor: addButtonClicked ? 'cornflowerblue' : (updButtonClicked ? 'darkgray' : (delButtonClicked ? 'red' : 'black')), marginBottom: '4px' }}
                                                type='primary'
                                                onClick={onUUserAccess}
                                            >
                                                Manage User Access
                                            </Button>
                                        </Form.Item>
                                    </Col>
                                    <Col span={18} />
                                </Row>
                                <Row gutter={16} className='retainbi-compact-row' style={{ visibility: addButtonClicked ? 'hidden' : 'visible' }}>
                                    <Col span={1} />
                                    <Col span={22}>
                                        <div>
                                            <Divider style={{ borderTop: '1px solid black', margin: '1px' }} />
                                            <Text italic style={{ color: 'gray' }}>
                                                NOTE: Users may be linked directly (via Foreign Key) to Tenant User Maps, Report Securities, DataSet Securities, and more.
                                            </Text>
                                        </div>
                                    </Col>
                                    <Col span={1} />
                                </Row>
                            </div>
                        </Col>
                        <Col span={1} />
                    </Row>
                    <Row gutter={16} className='retainbi-compact-row' style={{ paddingTop: '8px' }}>
                        <Col span={17} />
                        <Col span={3}>
                            <div style={{ textAlign: 'right' }}>
                                <Form.Item
                                    name='uApplyButton'
                                    style={{ marginBottom: '0px' }}
                                >
                                    <Button
                                        htmlType='submit'
                                        style={{width: '95%', backgroundColor: addButtonClicked ? 'cornflowerblue' : (updButtonClicked ? 'darkgray' : (delButtonClicked ? 'red' : 'black')) }}
                                        type='primary'
                                    >
                                        {addButtonClicked ? 'Add User' : (updButtonClicked ? 'Update User' : (delButtonClicked ? 'Delete User' : ''))}
                                    </Button>
                                </Form.Item>
                            </div>
                        </Col>
                        <Col span={3}>
                            <div style={{ textAlign: 'right' }}>
                                <Form.Item
                                    name='uCancelButton'
                                    style={{ marginBottom: '0px' }}
                                >
                                    <Button
                                        style={{width: '95%'}}
                                        type='default'
                                        onClick={onUCancel}
                                    >
                                        {addButtonClicked ? 'Cancel Add' : (updButtonClicked ? 'Cancel Update' : (delButtonClicked ? 'Cancel Delete' : ''))}
                                    </Button>
                                </Form.Item>
                            </div>
                        </Col>
                        <Col span={1} />
                    </Row>
                </Form>
            </Spin>
        </Card>
    );
};

export default AUDUser;
